import { resolveCameraPose } from '@toolproof-core/visualization/_pure/camera-pose';
import type { CameraFraming } from '@toolproof-core/visualization/_contract/camera';
import type { Vector3Tuple } from '@toolproof-core/visualization/_contract/geometry';
import type { SpaceDerivationFrame } from '@toolproof-core/visualization/derivation/_contract/derivation';

import { deriveWorkflowOverviewEnvelope } from '@/workflow/_pure/derivation/overviewEnvelope';

export const OVERVIEW_VIEW_KEY = 'overview';
export const OVERVIEW_PADDING = 1.15;

/** Overview framing that keeps the configured view direction and fits the frame's effective geometry. */
export function deriveOverviewFraming(
    frame: SpaceDerivationFrame,
    padding = OVERVIEW_PADDING,
    extentPoints?: Vector3Tuple[],
): CameraFraming | undefined {
    if (!Number.isFinite(padding) || padding < 1) {
        throw new Error('Workflow overviewPadding must be finite and at least one.');
    }
    // Bounds come from the renderer's own placement and geometry specs; unsupported kinds throw.
    const bounds = deriveWorkflowOverviewEnvelope(frame, extentPoints);
    if (!bounds) return undefined;

    const toDirection = (view: { position: Vector3Tuple; target: Vector3Tuple }): Vector3Tuple => [
        view.position[0] - view.target[0],
        view.position[1] - view.target[1],
        view.position[2] - view.target[2],
    ];
    const configuredDirection = toDirection(resolveCameraPose(frame.spaceRenderOptions));
    const direction = Math.hypot(...configuredDirection) > 0 && configuredDirection.every(Number.isFinite)
        ? configuredDirection
        : toDirection(resolveCameraPose(undefined));

    return {
        framingKind: 'extent',
        bounds,
        direction,
        padding,
    };
}

/**
 * Publishes the camera initialization key only once the overview it initializes to exists, so the
 * camera never fits an empty frame and then skips the real content. A mode-declared key wins.
 */
export function withCameraInitializationKey(
    frame: SpaceDerivationFrame,
    initializationKey: string | undefined,
): SpaceDerivationFrame {
    const spaceRenderOptions = frame.spaceRenderOptions ?? {};
    const camera = spaceRenderOptions.camera ?? {};
    if (!initializationKey || camera.initializationKey !== undefined) return frame;
    if (camera.graph?.views[OVERVIEW_VIEW_KEY] === undefined) return frame;

    return {
        ...frame,
        spaceRenderOptions: {
            ...spaceRenderOptions,
            camera: { ...camera, initializationKey },
        },
    };
}

/** Publishes the derived overview view unless the frame already declares one explicitly. */
export function withOverviewView(
    frame: SpaceDerivationFrame,
    padding = OVERVIEW_PADDING,
    extentPoints?: Vector3Tuple[],
): SpaceDerivationFrame {
    const spaceRenderOptions = frame.spaceRenderOptions ?? {};
    const camera = spaceRenderOptions.camera ?? {};
    if (camera.graph?.views[OVERVIEW_VIEW_KEY] !== undefined) return frame;

    const overview = deriveOverviewFraming(frame, padding, extentPoints);
    if (!overview) return frame;

    return {
        ...frame,
        spaceRenderOptions: {
            ...spaceRenderOptions,
            camera: {
                ...camera,
                graph: {
                    stations: { ...camera.graph?.stations, overview: { stationKind: 'framing', framing: overview } },
                    views: { ...camera.graph?.views, overview: { station: 'overview',
                        target: { anchorKind: 'position', position: overview.bounds.center } } },
                    paths: [
                        ...camera.graph?.paths ?? [],
                        ...Object.keys(camera.graph?.views ?? {}).filter((id) => id !== 'overview').flatMap((id) => [
                            { pathId: `overview->${id}`, from: 'overview', to: id, pathKind: 'straight' as const },
                            { pathId: `${id}->overview`, from: id, to: 'overview', pathKind: 'straight' as const },
                        ]),
                    ],
                },
            },
        },
    };
}
